import { Box, Chip, ChipProps, Tooltip, Typography } from "@mui/material";
import { LinearIssueStatusMap } from "hooks/useLinearIssueStatuses";
import React from "react";
import {
  handleLinearTagClick,
  handleLinearTagDoubleClick,
  isLinearIssue,
} from "routes/Staff/WorkflowConfig/linearTagHelpers";
import { getLinearStatusColor } from "util/linearStatusColors";

interface LinearTagChipProps {
  tag: string;
  linearStatusMap: LinearIssueStatusMap;
  onNormalClick?: () => void;
  sx?: ChipProps["sx"];
  size?: "small" | "medium";
  onDelete?: () => void;
  disabled?: boolean;
}

/**
 * Reusable chip component for displaying tags with Linear integration.
 * Automatically detects Linear tags (ENG-XXX pattern) and applies appropriate styling/handlers.
 * Works seamlessly with mixed collections of Linear and regular tags.
 */
export function LinearTagChip({
  tag,
  linearStatusMap,
  onNormalClick,
  sx,
  size = "small",
  onDelete,
  disabled = false,
}: LinearTagChipProps) {
  const isLinear = isLinearIssue(tag);
  const issueInfo = isLinear ? linearStatusMap[tag.toUpperCase()] : null;
  const backgroundColor = issueInfo ? getLinearStatusColor(issueInfo.status) : undefined;

  const chip = (
    <Chip
      label={tag}
      size={size}
      onDelete={onDelete}
      disabled={disabled}
      onClick={(e) => handleLinearTagClick(e, tag, linearStatusMap, onNormalClick)}
      onDoubleClick={(e) => handleLinearTagDoubleClick(e, tag, linearStatusMap)}
      sx={{
        height: size === "small" ? 20 : undefined,
        fontSize: size === "small" ? "0.7rem" : undefined,
        flexShrink: 0,
        maxWidth: size === "small" ? 80 : undefined,
        backgroundColor,
        cursor: isLinear && issueInfo && !disabled ? "pointer" : "default",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "& .MuiChip-label": {
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          padding: size === "small" ? "0 8px" : undefined,
          display: "flex",
          alignItems: "center",
          lineHeight: "normal",
        },
        ...sx,
      }}
    />
  );

  // Show rich tooltip for Linear issues
  if (issueInfo) {
    const tooltipContent = (
      <Box sx={{ p: 0.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography variant="caption" fontWeight="bold">
            {tag.toUpperCase()}
          </Typography>
          <Chip
            label={issueInfo.status}
            size="small"
            sx={{
              height: 18,
              fontSize: "0.65rem",
              backgroundColor: getLinearStatusColor(issueInfo.status),
            }}
          />
          {issueInfo.assigneeName && (
            <Typography variant="caption">{issueInfo.assigneeName}</Typography>
          )}
        </Box>
        <Typography variant="body2">{issueInfo.title}</Typography>
        {issueInfo.isReleased && (
          <Typography
            variant="caption"
            fontStyle="italic"
            display="block"
            sx={{ mt: 1, color: "#90EE90" }}
          >
            * released
          </Typography>
        )}
      </Box>
    );

    return <Tooltip title={tooltipContent}>{chip}</Tooltip>;
  }

  return chip;
}
